import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaHourglassHalf,
  FaTimesCircle,
  FaEye,
  FaDownload,
  FaPhone,
  FaEnvelope,
  FaHome,
  FaSearch,
  FaComments,
  FaBan,
  FaPaperPlane,
  FaCheck
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getPropertyImage } from '../utils/imageHelper';
import { downloadBookingReceipt } from '../utils/bookingReceipt';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import { sendGuestToHostMessage, getGuestConversation, updateGuestSentMessageStatus } from '../services/messagingService';
import { getApiUrl } from '../config/api';
import './TripsPage.css';

const TripsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'confirmed', 'declined', 'cancelled'
  const [bookingTypeFilter, setBookingTypeFilter] = useState('all'); // 'all', 'stay', 'meeting'
  const [searchQuery, setSearchQuery] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [conversationMessages, setConversationMessages] = useState([]);
  const chatBodyRef = useRef(null);
  const [cancelReasonType, setCancelReasonType] = useState('');
  const [cancelReasonCustom, setCancelReasonCustom] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [receiptBooking, setReceiptBooking] = useState(null);

  const CANCELLATION_REASONS = [
    { value: '', label: 'Select a reason...' },
    { value: 'change_of_plans', label: 'Change of plans' },
    { value: 'found_alternative', label: 'Found alternative accommodation' },
    { value: 'travel_dates_changed', label: 'Travel dates changed' },
    { value: 'unexpected_circumstances', label: 'Unexpected circumstances' },
    { value: 'property_not_suitable', label: 'Property no longer suitable' },
    { value: 'duplicate_booking', label: 'Duplicate booking' },
    { value: 'other', label: 'Other (please specify below)' }
  ];

  useEffect(() => {
    // Load current user
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }

    // Load bookings from API
    loadBookings();

    // Poll for booking updates every 30 seconds (less disruptive)
    const pollingInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        loadBookings();
      }
    }, 30000); // Check every 30 seconds for status updates

    return () => clearInterval(pollingInterval);
  }, []);

  useEffect(() => {
    if (showMessageModal && selectedBooking) {
      loadConversation(selectedBooking);
    }
  }, [showMessageModal, selectedBooking]);

  useEffect(() => {
    if (chatBodyRef.current && conversationMessages.length > 0) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [conversationMessages]);

  useEffect(() => {
    const handleStorage = () => {
      if (showMessageModal && selectedBooking) {
        loadConversation(selectedBooking);
      }
    };
    const handleMessagesUpdated = () => {
      if (showMessageModal && selectedBooking) {
        loadConversation(selectedBooking);
      }
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('messagesUpdated', handleMessagesUpdated);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('messagesUpdated', handleMessagesUpdated);
    };
  }, [showMessageModal, selectedBooking]);

  // Poll for host replies when message modal is open (20s to reduce API rate limit pressure)
  useEffect(() => {
    if (!showMessageModal || !selectedBooking) return;
    const interval = setInterval(() => {
      loadConversation(selectedBooking);
    }, 20000);
    return () => clearInterval(interval);
  }, [showMessageModal, selectedBooking]);

  useEffect(() => {
    // Filter bookings based on status and search query
    let filtered = bookings;

    if (bookingTypeFilter !== 'all') {
      filtered = filtered.filter((booking) => booking.bookingType === bookingTypeFilter);
    }

    if (filterStatus !== 'all') {
      if (filterStatus === 'confirmed') {
        // Show both 'confirmed' and 'approved' status
        filtered = filtered.filter(booking => 
          booking.status === 'confirmed' || booking.status === 'approved'
        );
      } else {
        filtered = filtered.filter(booking => booking.status === filterStatus);
      }
    }

    if (searchQuery) {
      filtered = filtered.filter(booking =>
        booking.property.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.property.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.bookingId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(booking.staffDetails?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(booking.companySnapshot?.name || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  }, [bookingTypeFilter, filterStatus, searchQuery, bookings]);

  const loadBookings = async () => {
    // Only show loading spinner on initial load, not on refresh
    if (bookings.length === 0) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.log('No token found, showing demo bookings');
        setBookings([]);
        setFilteredBookings([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      // Fetch stay + meeting bookings in parallel
      const [stayResponse, meetingResponse] = await Promise.all([
        fetch(getApiUrl('/api/bookings/stay/my-bookings'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(getApiUrl('/api/meetings/bookings/my-bookings'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      const stayResult = await stayResponse.json();
      const meetingResult = await meetingResponse.json();

      const stayBookings = (stayResult.success && Array.isArray(stayResult.bookings))
        ? stayResult.bookings
        : [];
      const meetingBookings = Array.isArray(meetingResult) ? meetingResult : [];

      const transformedStayBookings = stayBookings.map((booking) => {
        const populatedProperty = booking?.property && typeof booking.property === 'object'
          ? booking.property
          : null;
        const snapshot = booking?.propertySnapshot || {};

        return {
          bookingId: booking._id,
          bookingType: 'stay',
          status: booking.status,
          paymentMethod: booking.paymentMethod || 'payAtHotel',
          paymentStatus: booking.paymentStatus || null,
          property: {
            _id: populatedProperty?._id || booking?.property || null,
            name: populatedProperty?.name || snapshot.name || 'Property',
            city: populatedProperty?.city || snapshot.city || 'Unknown',
            state: populatedProperty?.state || snapshot.state || 'Karnataka',
            image:
              populatedProperty?.images?.[0] ||
              populatedProperty?.image ||
              snapshot.image ||
              'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80',
            pricePerNight: populatedProperty?.pricePerNight || snapshot.pricePerNight || 0,
            hostId: populatedProperty?.hostId?._id ?? populatedProperty?.hostId ?? null
          },
          checkIn: booking.checkIn,
          checkOut: booking.checkOut,
          guests: booking.guests?.adults || 1,
          totalPrice: booking.totalPrice,
          guestInfo: {
            firstName: currentUser?.name?.split(' ')[0] || 'Guest',
            lastName: currentUser?.name?.split(' ')[1] || '',
            email: currentUser?.email || '',
            phone: currentUser?.phone || ''
          },
          companySnapshot: booking.companySnapshot || null,
          staffDetails: booking.staffDetails || null,
          createdAt: booking.createdAt
        };
      });

      const transformedMeetingBookings = meetingBookings.map((booking) => {
        const room = booking?.room && typeof booking.room === 'object' ? booking.room : {};
        const startTime = booking.startTime || booking.checkIn || booking.createdAt;
        const endTime = booking.endTime || booking.checkOut || booking.startTime || booking.createdAt;
        const attendeesCount = Number(booking.attendees || booking.selectedMembers?.length || 1);

        return {
          bookingId: booking._id,
          bookingType: 'meeting',
          status: booking.status,
          paymentMethod: 'payAtHotel',
          paymentStatus: null,
          property: {
            _id: room._id || booking.room || null,
            name: room.name || booking.propertyName || 'Meeting Room',
            city: room.city || 'Meeting',
            state: room.state || 'Room',
            image: room.images?.[0] || room.image || 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80',
            pricePerNight: 0,
            hostId: room.userId || room.hostId || null
          },
          checkIn: startTime,
          checkOut: endTime,
          guests: attendeesCount,
          totalPrice: booking.totalPrice || 0,
          guestInfo: {
            firstName: currentUser?.name?.split(' ')[0] || 'Guest',
            lastName: currentUser?.name?.split(' ')[1] || '',
            email: currentUser?.email || '',
            phone: currentUser?.phone || ''
          },
          companySnapshot: booking.companySnapshot || null,
          staffDetails: booking.staffDetails || null,
          createdAt: booking.createdAt
        };
      });

      const transformedBookings = [...transformedStayBookings, ...transformedMeetingBookings]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

      if (transformedBookings.length > 0) {
        console.log('Loaded', transformedBookings.length, 'bookings from API');
        setBookings(transformedBookings);
        setFilteredBookings(transformedBookings);
      } else {
        console.log('No bookings found');
        setBookings([]);
        setFilteredBookings([]);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      setBookings([]);
      setFilteredBookings([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: <FaHourglassHalf />, text: 'Pending Approval', class: 'pending' },
      confirmed: { icon: <FaCheckCircle />, text: 'Confirmed', class: 'approved' },
      approved: { icon: <FaCheckCircle />, text: 'Approved', class: 'approved' },
      declined: { icon: <FaTimesCircle />, text: 'Declined', class: 'declined' },
      cancelled: { icon: <FaTimesCircle />, text: 'Cancelled', class: 'declined' }
    };
    return badges[status] || badges.pending;
  };

  const calculateNights = (checkIn, checkOut) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewDetails = (booking) => {
    // Navigate to booking details or show modal
    navigate(`/booking-details/${booking.bookingId}`, { state: { booking } });
  };

  const handleDownload = (booking) => setReceiptBooking(booking);

  const handleSendMessage = (booking) => {
    setSelectedBooking(booking);
    setShowMessageModal(true);
    setMessageText('');
    loadConversation(booking);
  };

  const loadConversation = async (booking) => {
    if (!booking) return;
    const userId = currentUser?._id || currentUser?.id || 'demo_user';
    const hostId = booking.property?.hostId?._id ?? booking.property?.hostId ?? 'host_demo';
    try {
      const messages = await getGuestConversation(userId, hostId, booking.bookingId);
      setConversationMessages(messages);
    } catch (e) {
      setConversationMessages([]);
    }
  };

  const handleMessageSubmit = async () => {
    if (!messageText.trim()) {
      toast.warning('Please enter a message');
      return;
    }

    setSendingMessage(true);
    try {
      const userId = currentUser?._id || currentUser?.id || 'demo_user';
      const hostIdRaw = selectedBooking.property?.hostId?._id ?? selectedBooking.property?.hostId;
      const hostId = hostIdRaw != null ? String(hostIdRaw) : null;
      if (!hostId) {
        setSendingMessage(false);
        toast.error('Could not determine host. Please refresh the page.');
        return;
      }
      const guestName = currentUser?.name || `${selectedBooking.guestInfo?.firstName || ''} ${selectedBooking.guestInfo?.lastName || ''}`.trim();
      
      const result = await sendGuestToHostMessage({
        guestId: userId,
        hostId,
        bookingId: selectedBooking.bookingId,
        propertyName: selectedBooking.property.name,
        message: messageText,
        guestName
      });

      if (result.success) {
        setMessageText('');
        loadConversation(selectedBooking);
        setTimeout(() => {
          updateGuestSentMessageStatus(result.message.id, 'delivered');
          loadConversation(selectedBooking);
        }, 1500);
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const MessageTicks = ({ status }) => {
    if (status === 'read') {
      return (
        <span className="chat-ticks chat-ticks--read" title="Seen">
          <FaCheck /><FaCheck />
        </span>
      );
    }
    if (status === 'delivered') {
      return (
        <span className="chat-ticks chat-ticks--delivered" title="Delivered">
          <FaCheck /><FaCheck />
        </span>
      );
    }
    return (
      <span className="chat-ticks chat-ticks--sent" title="Sent">
        <FaCheck />
      </span>
    );
  };

  const handleCancelBooking = (booking) => {
    setSelectedBooking(booking);
    setShowCancelModal(true);
    setCancelReasonType('');
    setCancelReasonCustom('');
  };

  const getCancelReasonForSubmit = () => {
    if (cancelReasonType === 'other') {
      return cancelReasonCustom.trim();
    }
    const selected = CANCELLATION_REASONS.find(r => r.value === cancelReasonType);
    return selected ? selected.label : '';
  };

  const isCancelReasonValid = () => {
    if (cancelReasonType === 'other') {
      return cancelReasonCustom.trim().length > 0;
    }
    return cancelReasonType && cancelReasonType !== '';
  };

  const handleCancelSubmit = async () => {
    const reason = getCancelReasonForSubmit();
    if (!reason) {
      toast.warning('Please select a cancellation reason or provide details for "Other"');
      return;
    }

    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/bookings/stay/${selectedBooking.bookingId}/cancel`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Booking cancelled successfully!');
        setShowCancelModal(false);
        setCancelReasonType('');
        setCancelReasonCustom('');
        setSelectedBooking(null);
        // Reload bookings to show updated status
        loadBookings();
      } else {
        toast.error(result.message || 'Failed to cancel booking. Please try again.');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="trips-page">
        <Navbar />
        <div className="trips-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trips-page">
      <Navbar />
      
      <div className="trips-container">
        {/* Header */}
        <div className="trips-header">
          <div className="header-content">
            <h1>My Trips</h1>
            <p>View and manage all your bookings</p>
            {refreshing && (
              <span style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginLeft: '10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <span style={{ 
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  border: '2px solid #ddd',
                  borderTopColor: '#003580',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></span>
                Updating...
              </span>
            )}
          </div>
          <button className="btn-back-home" onClick={() => navigate('/')}>
            <FaHome /> Back to Home
          </button>
        </div>

        {/* Filters and Search */}
        <div className="trips-filters">
          <div className="booking-type-toggle" role="tablist" aria-label="Filter bookings by type">
            <button
              type="button"
              className={`type-toggle-btn ${bookingTypeFilter === 'stay' ? 'active' : ''}`}
              onClick={() => setBookingTypeFilter('stay')}
            >
              Hotel Room ({bookings.filter((b) => b.bookingType === 'stay').length})
            </button>
            <button
              type="button"
              className={`type-toggle-btn ${bookingTypeFilter === 'meeting' ? 'active' : ''}`}
              onClick={() => setBookingTypeFilter('meeting')}
            >
              Meeting Room ({bookings.filter((b) => b.bookingType === 'meeting').length})
            </button>
          </div>

          <div className="trips-filter-row">
            <label htmlFor="trips-status-filter" className="trips-filter-label">Status</label>
            <select
              id="trips-status-filter"
              className="trips-status-dropdown"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              aria-label="Filter bookings by status"
            >
              <option value="all">All Bookings ({bookings.length})</option>
              <option value="pending">Pending ({bookings.filter(b => b.status === 'pending').length})</option>
              <option value="confirmed">Confirmed ({bookings.filter(b => b.status === 'confirmed' || b.status === 'approved').length})</option>
              <option value="declined">Declined ({bookings.filter(b => b.status === 'declined').length})</option>
              <option value="cancelled">Cancelled ({bookings.filter(b => b.status === 'cancelled').length})</option>
            </select>
          </div>

          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search by property name, city, or booking ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Bookings List */}
        {filteredBookings.length === 0 ? (
          <div className="no-bookings">
            <div className="no-bookings-icon">📅</div>
            <h2>No bookings found</h2>
            <p>
              {searchQuery || filterStatus !== 'all' || bookingTypeFilter !== 'all'
                ? 'Try adjusting your filters or search query'
                : "You haven't made any bookings yet"}
            </p>
            <button className="btn-explore" onClick={() => navigate('/')}>
              Explore Properties
            </button>
          </div>
        ) : (
          <div className="bookings-list">
            {filteredBookings.map((booking) => {
              const statusBadge = getStatusBadge(booking.status);
              const nights = calculateNights(booking.checkIn, booking.checkOut);

              return (
                <div key={booking.bookingId} className="booking-card">
                  {/* Property Image */}
                  <div className="booking-image">
                    <img
                      src={getPropertyImage(booking.property)}
                      alt={booking.property.name}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80';
                      }}
                    />
                    <div className={`status-badge ${statusBadge.class}`}>
                      {statusBadge.icon}
                      <span>{statusBadge.text}</span>
                    </div>
                  </div>

                  {/* Booking Details - Middle Column */}
                  <div className="booking-details">
                    <h3 className="property-name">{booking.property.name}</h3>
                    <p className="property-location">
                      <FaMapMarkerAlt /> {booking.property.city}, {booking.property.state}
                    </p>
                    <div className="booking-id">
                      <span className="id-label">Booking ID</span>
                      <span className="id-value">{booking.bookingId}</span>
                    </div>
                    <div className="booking-info-grid">
                      <div className="info-item">
                        <FaCalendarAlt className="info-icon" />
                        <div>
                          <span className="info-label">Check-in</span>
                          <span className="info-value">{formatDate(booking.checkIn)}</span>
                        </div>
                      </div>
                      <div className="info-item">
                        <FaCalendarAlt className="info-icon" />
                        <div>
                          <span className="info-label">Check-out</span>
                          <span className="info-value">{formatDate(booking.checkOut)}</span>
                        </div>
                      </div>
                      <div className="info-item">
                        <FaClock className="info-icon" />
                        <div>
                          <span className="info-label">Duration</span>
                          <span className="info-value">{nights} {nights === 1 ? 'Night' : 'Nights'}</span>
                        </div>
                      </div>
                      <div className="info-item">
                        <FaUsers className="info-icon" />
                        <div>
                          <span className="info-label">Guests</span>
                          <span className="info-value">{booking.guests} {booking.guests === 1 ? 'Guest' : 'Guests'}</span>
                        </div>
                      </div>
                    </div>
                    {booking.staffDetails?.name && (
                      <div className="booking-id">
                        <span className="id-label">Booked By</span>
                        <span className="id-value">
                          {booking.staffDetails.name}
                          {booking.staffDetails.role ? ` (${booking.staffDetails.role})` : ''}
                          {booking.staffDetails.employeeId ? ` · ${booking.staffDetails.employeeId}` : ''}
                        </span>
                      </div>
                    )}
                    {booking.companySnapshot?.name && (
                      <div className="booking-id">
                        <span className="id-label">Company</span>
                        <span className="id-value">{booking.companySnapshot.name}</span>
                      </div>
                    )}
                  </div>

                  {/* Price & Actions - Right Column */}
                  <div className="booking-price-block">
                    <div className="price-section">
                      <span className="price-label">Total Amount</span>
                      <span className="price-value">₹{booking.totalPrice?.toLocaleString()}</span>
                    </div>
                    <div className="payment-method-label">
                      {booking.paymentMethod === 'online' && booking.paymentStatus === 'paid'
                        ? 'Paid online'
                        : 'Pay at hotel at check-in'}
                    </div>
                    <div className="action-buttons">
                      {booking.bookingType !== 'meeting' && (booking.status === 'pending' || booking.status === 'confirmed' || booking.status === 'approved') && (
                        <button
                          className="btn-action btn-cancel"
                          onClick={() => handleCancelBooking(booking)}
                          title="Cancel booking"
                        >
                          <FaBan /> Cancel
                        </button>
                      )}
                      <button
                        className="btn-action btn-message"
                        onClick={() => handleSendMessage(booking)}
                        title="Send message to host"
                      >
                        <FaComments /> Message
                      </button>
                      <button
                        className="btn-action btn-view"
                        onClick={() => handleViewDetails(booking)}
                      >
                        <FaEye /> View
                      </button>
                      <button
                        className="btn-action btn-download"
                        onClick={() => handleDownload(booking)}
                      >
                        <FaDownload />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Help Section */}
        <div className="help-section">
          <h3>Need Help?</h3>
          <div className="help-options">
            <div className="help-option">
              <FaPhone />
              <div>
                <strong>Call Us</strong>
                <p>24/7 Customer Support</p>
              </div>
            </div>
            <div className="help-option">
              <FaEnvelope />
              <div>
                <strong>Email Us</strong>
                <p>support@bookandstay.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Receipt Preview Modal */}
      {receiptBooking && (
        <ReceiptPreviewModal
          booking={receiptBooking}
          onClose={() => setReceiptBooking(null)}
          onDownload={downloadBookingReceipt}
        />
      )}

      {/* Message Modal - Chat Box Style */}
      {showMessageModal && selectedBooking && (
        <div className="modal-overlay modal-overlay--chat" onClick={() => setShowMessageModal(false)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-header">
              <div className="chat-header-info">
                <h3 className="chat-title">Message Host</h3>
                <p className="chat-subtitle">{selectedBooking.property.name}</p>
                <span className="chat-booking-id">ID: {selectedBooking.bookingId}</span>
              </div>
              <button className="chat-close" onClick={() => setShowMessageModal(false)} aria-label="Close">×</button>
            </div>
            <div className="chat-body" ref={chatBodyRef}>
              {conversationMessages.length === 0 ? (
                <div className="chat-empty-state">
                  <FaComments className="chat-empty-icon" />
                  <p>Start a conversation with your host</p>
                  <span>Type your message below and hit send</span>
                </div>
              ) : (
                <div className="chat-messages">
                  {conversationMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`chat-bubble chat-bubble--${msg.from}`}
                    >
                      <div className="chat-bubble-content">
                        <p className="chat-bubble-text">{msg.message}</p>
                        <div className="chat-bubble-meta">
                          <span className="chat-bubble-time">{formatMessageTime(msg.timestamp)}</span>
                          {msg.from === 'guest' && (
                            <MessageTicks status={msg.status || 'sent'} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="chat-input-bar">
              <textarea
                className="chat-input"
                placeholder="Type a message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                rows={2}
                disabled={sendingMessage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleMessageSubmit();
                  }
                }}
              />
              <button
                className="chat-send-btn"
                onClick={handleMessageSubmit}
                disabled={sendingMessage || !messageText.trim()}
                aria-label="Send message"
              >
                {sendingMessage ? (
                  <span className="chat-send-spinner" />
                ) : (
                  <FaPaperPlane />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal */}
      {showCancelModal && selectedBooking && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ background: '#ef4444' }}>
              <h3>❌ Cancel Booking</h3>
              <button className="btn-close" onClick={() => setShowCancelModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="booking-info-modal" style={{ background: '#fff3cd', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                <p style={{ margin: '5px 0' }}><strong>Property:</strong> {selectedBooking.property.name}</p>
                <p style={{ margin: '5px 0' }}><strong>Booking ID:</strong> {selectedBooking.bookingId}</p>
                <p style={{ margin: '5px 0' }}><strong>Check-in:</strong> {formatDate(selectedBooking.checkIn)}</p>
                <p style={{ margin: '5px 0' }}><strong>Check-out:</strong> {formatDate(selectedBooking.checkOut)}</p>
              </div>
              <p style={{ marginBottom: '10px', color: '#666' }}>
                Please provide a reason for cancellation. The host will be notified.
              </p>
              <select
                className="cancel-reason-select"
                value={cancelReasonType}
                onChange={(e) => setCancelReasonType(e.target.value)}
                disabled={cancelling}
              >
                {CANCELLATION_REASONS.map((r) => (
                  <option key={r.value || 'empty'} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              {cancelReasonType === 'other' && (
                <textarea
                  className="message-textarea"
                  placeholder="Please specify your reason for cancellation..."
                  value={cancelReasonCustom}
                  onChange={(e) => setCancelReasonCustom(e.target.value)}
                  rows="3"
                  disabled={cancelling}
                  style={{ marginTop: '12px' }}
                />
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
              >
                Keep Booking
              </button>
              <button
                className="btn-send"
                onClick={handleCancelSubmit}
                disabled={cancelling || !isCancelReasonValid()}
                style={{ background: '#ef4444' }}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripsPage;

